/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useEffect, useRef, useState, type RefObject } from 'react';

export interface UseIntersectionObserverOptions {
    /** Detach the observer after the element first appears, so consumers fire exactly once. */
    useOnce?: boolean;
    root?: Element | null;
    rootMargin?: string;
    threshold?: number | number[];
}

// RGBARRY-CUSTOMIZATION: keep the observer stable across state-driven rerenders and document SSR behavior.
/**
 * Returns whether the referenced element is on screen. The initial/SSR value is
 * `false`; after mount it fails open to `true` when `IntersectionObserver` is
 * unsupported, so viewport-gated side effects still fire. Mirrors PWA Kit's
 * `useIntersectionObserver`.
 */
export function useIntersectionObserver(
    ref: RefObject<Element | null>,
    { useOnce, root, rootMargin, threshold }: UseIntersectionObserverOptions = {}
): boolean {
    const [isIntersecting, setIntersecting] = useState(false);
    const thresholdKey = Array.isArray(threshold) ? threshold.join(',') : threshold;
    const observerRef = useRef<IntersectionObserver | null>(null);
    const observedNodeRef = useRef<Element | null>(null);
    const observedOptionsRef = useRef<
        | {
              root?: Element | null;
              rootMargin?: string;
              thresholdKey?: number | string;
              useOnce?: boolean;
          }
        | undefined
    >(undefined);

    // oxlint-disable-next-line react-hooks/exhaustive-deps -- compare ref.current after every commit so replacement nodes rebind without reconnecting unchanged nodes
    useEffect(() => {
        const node = ref.current;
        const previousOptions = observedOptionsRef.current;
        const optionsUnchanged =
            previousOptions !== undefined &&
            previousOptions.root === root &&
            previousOptions.rootMargin === rootMargin &&
            previousOptions.thresholdKey === thresholdKey &&
            previousOptions.useOnce === useOnce;

        if (node === observedNodeRef.current && optionsUnchanged) return;

        observerRef.current?.disconnect();
        observerRef.current = null;
        observedNodeRef.current = node;
        observedOptionsRef.current = { root, rootMargin, thresholdKey, useOnce };
        if (!node) return;

        const observerThreshold =
            typeof thresholdKey === 'string'
                ? thresholdKey === ''
                    ? []
                    : thresholdKey.split(',').map(Number)
                : thresholdKey;

        // Fail open when the API is unavailable (SSR / old browsers).
        if (typeof IntersectionObserver === 'undefined') {
            setIntersecting(true);
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                const onScreen = entry.isIntersecting;
                setIntersecting(onScreen);
                if (useOnce && onScreen) {
                    observer.disconnect();
                }
            },
            { root, rootMargin, threshold: observerThreshold }
        );

        observerRef.current = observer;
        observer.observe(node);
    });

    useEffect(
        () => () => {
            observerRef.current?.disconnect();
            observerRef.current = null;
            observedNodeRef.current = null;
            observedOptionsRef.current = undefined;
        },
        []
    );

    return isIntersecting;
}
// END RGBARRY-CUSTOMIZATION
